/** Route comments to linked tasks or the frame's creating agent. */
import { useState, useMemo, useCallback } from "react";
import { useProjectStore } from "@/stores/project-store";
import { useAgentStore } from "@/stores/agent-store";
import { useTasksStore } from "@/stores/tasks-store";
import { useNotificationStore } from "@/stores/notification-store";
import { postComment } from "@/api/tasks";
import { getFrameReplyRoute } from "@/api/spaces";
import { dispatchUserMessage } from "@/hooks/use-dispatch-user-message";
import { currentFrameCenter } from "@/components/shapes/conversation-frame/conversation-frame-utils";
import { agentColorByIndex } from "@/lib/agent-colors";
import {
  filterFrameMentionOptions,
  parseFrameMentions,
  type FrameMentionOption,
  type MentionedFrame,
} from "@/lib/frame-mentions";
import { useFrameMentionOptions } from "@/hooks/use-frame-mention-options";
import { translate } from "@/i18n";
import { hooksMessages } from "@/i18n/messages/hooks";

const DONE_STATUSES = new Set(["done"]);
const RECENT_COMPLETION_WINDOW_MS = 10 * 60 * 1000;

/* ---------- Types ---------- */

export interface AgentOption {
  id: string;
  name: string;
  color: string;
  avatar_seed?: string;
}

export type CommentMentionOption =
  | { kind: "agent"; agent: AgentOption }
  | { kind: "frame"; frame: FrameMentionOption };

export interface LinkedTask {
  id: string;
  title: string;
  status: string;
  ticketDisplay: string | null;
  updatedAt: number;
}

interface UseCommentActionOptions {
  frameId: string;
  frameTitle: string;
  frameType: string;
}

/** Submission payload passed to the post/create helpers. */
interface PendingSubmission {
  text: string;
  selectedAgent: AgentOption | null;
  imageIds: string[] | undefined;
  mentionedFrames: MentionedFrame[];
}

interface UseCommentActionReturn {
  /* @mention state */
  text: string;
  setText: (val: string) => void;
  selectedAgent: AgentOption | null;
  showMentions: boolean;
  mentionFilter: string;
  mentionIndex: number;
  filteredAgents: AgentOption[];
  mentionOptions: CommentMentionOption[];
  handleTextChange: (val: string) => void;
  handleMentionKeyDown: (key: string) => boolean;
  selectAgent: (agent: AgentOption) => void;
  selectMentionOption: (option: CommentMentionOption) => void;
  clearAgent: () => void;

  /* task resolution */
  linkedTasks: LinkedTask[];
  selectedTaskId: string | null;
  setSelectedTaskId: (id: string | null) => void;

  /* submission */
  canSend: boolean;
  isSending: boolean;
  submit: (opts?: { imageIds?: string[] }) => Promise<boolean>;
}

export function useCommentAction({
  frameId,
  frameTitle,
  frameType,
}: UseCommentActionOptions): UseCommentActionReturn {
  // Use reactive subscriptions (not getState()) so values stay fresh
  const currentProject = useProjectStore((s) => s.currentProject);
  const currentSpace = useProjectStore((s) => s.currentSpace);
  const canAssignAgent = useAgentStore(
    (state) => state.specialistCreationEnabled,
  );
  const agents = useAgentStore((s) => s.agents);
  const allTasks = useTasksStore((s) => s.tasks);

  /* @mention state */
  const [text, setText] = useState("");
  const [selectedAgent, setSelectedAgent] = useState<AgentOption | null>(null);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  /* Agent options */
  const agentOptions: AgentOption[] = useMemo(
    () =>
      (canAssignAgent ? agents : []).map((pa, i) => ({
        id: pa.agent_template,
        name: pa.template_name || "Agent",
        color: pa.template_color || agentColorByIndex(i),
        avatar_seed: pa.template_avatar_seed,
      })),
    [agents, canAssignAgent],
  );

  const filteredAgents = useMemo(() => {
    if (!mentionFilter) return agentOptions;
    const q = mentionFilter.toLowerCase();
    return agentOptions.filter((a) => a.name.toLowerCase().includes(q));
  }, [agentOptions, mentionFilter]);

  const frameOptions = useFrameMentionOptions();

  const mentionOptions = useMemo<CommentMentionOption[]>(() => {
    const agentMentionOptions = filteredAgents.map((agent) => ({
      kind: "agent" as const,
      agent,
    }));
    const frameMentionOptions = filterFrameMentionOptions(
      frameOptions,
      mentionFilter,
    ).map((frame) => ({
      kind: "frame" as const,
      frame,
    }));
    return [...agentMentionOptions, ...frameMentionOptions];
  }, [filteredAgents, frameOptions, mentionFilter]);

  /* Linked tasks — active tasks where this frame is the source or a deliverable,
   * sorted by most recently updated first. Recently-completed (<10min) tasks
   * stay visible so a follow-up comment reopens the thread instead of
   * spawning a brand-new task. */
  const linkedTasks: LinkedTask[] = useMemo(() => {
    const now = Date.now();
    return allTasks
      .filter((t) => {
        const matchesFrame =
          t.frame === frameId || t.deliverable_frame_ids?.includes(frameId);
        if (!matchesFrame) return false;
        if (!DONE_STATUSES.has(t.status)) return true;
        const updatedAt = t.updated_at ? new Date(t.updated_at).getTime() : 0;
        return now - updatedAt <= RECENT_COMPLETION_WINDOW_MS;
      })
      .map((t) => ({
        id: t.id,
        title: t.title,
        status: t.status,
        ticketDisplay: t.ticket_display,
        updatedAt: t.updated_at ? new Date(t.updated_at).getTime() : 0,
      }))
      .sort((a, b) => b.updatedAt - a.updatedAt);
  }, [allTasks, frameId]);

  /* Text change with @mention detection */
  const handleTextChange = useCallback((val: string) => {
    setText(val);
    const match = val.match(/@(\S*)$/);
    if (match) {
      setShowMentions(true);
      setMentionFilter(match[1] ?? "");
      setMentionIndex(0);
    } else {
      setShowMentions(false);
      setMentionFilter("");
    }
  }, []);

  const applyMentionOption = useCallback((option: CommentMentionOption) => {
    setShowMentions(false);
    setMentionFilter("");
    if (option.kind === "agent") {
      setSelectedAgent(option.agent);
      setText((prev) => prev.replace(/@\S*$/, "").trimEnd());
      return;
    }
    setText((prev) =>
      prev.replace(/@\S*$/, `@${option.frame.label} `).trimEnd(),
    );
  }, []);

  /* Keyboard navigation for mention dropdown. Returns true if handled. */
  const handleMentionKeyDown = useCallback(
    (key: string): boolean => {
      if (!showMentions || mentionOptions.length === 0) return false;
      if (key === "ArrowDown") {
        setMentionIndex((i) => Math.min(i + 1, mentionOptions.length - 1));
        return true;
      }
      if (key === "ArrowUp") {
        setMentionIndex((i) => Math.max(i - 1, 0));
        return true;
      }
      if (key === "Enter" || key === "Tab") {
        const option = mentionOptions[mentionIndex];
        if (option) applyMentionOption(option);
        return true;
      }
      if (key === "Escape") {
        setShowMentions(false);
        return true;
      }
      return false;
    },
    [showMentions, mentionOptions, mentionIndex, applyMentionOption],
  );

  const selectAgent = useCallback((agent: AgentOption) => {
    setSelectedAgent(agent);
    setShowMentions(false);
    setMentionFilter("");
    setText((prev) => prev.replace(/@\S*$/, "").trimEnd());
  }, []);

  const selectMentionOption = useCallback(
    (option: CommentMentionOption) => {
      applyMentionOption(option);
    },
    [applyMentionOption],
  );

  const clearAgent = useCallback(() => {
    setSelectedAgent(null);
  }, []);

  // A comment can go to an existing task or to the agent for a decision.
  const canSend = text.trim().length > 0 && !isSending;

  /* Build frame-update instruction */
  const frameInstruction =
    `Update frame "${frameTitle}" with your results by default. ` +
    "Create a new frame only if the user explicitly asks for a " +
    "new/separate frame or says not to overwrite this one.";

  const postCommentOnTask = useCallback(
    async (taskId: string, payload: PendingSubmission) => {
      if (!currentProject) return;

      // Skip the `@{agent}` wire prefix when this task is already
      // assigned to that exact agent — the backend auto-spawn guard
      // treats self-mentions as a no-op, but keeping the literal in
      // the content would still clutter the comment thread and fire
      // analytics for "has_mentions". The UI chip stays visible.
      const targetTask = allTasks.find((t) => t.id === taskId);
      const assignedAgentId = targetTask?.assigned_agent_detail?.id ?? null;
      const suppressMentionPrefix =
        payload.selectedAgent !== null &&
        assignedAgentId === payload.selectedAgent.id;

      const parts: string[] = [];
      if (payload.selectedAgent && !suppressMentionPrefix) {
        parts.push(`@${payload.selectedAgent.name}`);
      }
      parts.push(`\n${payload.text.trim()}`);
      if (payload.mentionedFrames.length > 0) {
        parts.push("\nMentioned frames:");
        for (const frame of payload.mentionedFrames) {
          parts.push(
            `- ${frame.title} (${frame.frame_type}, ${frame.frame_id})`,
          );
        }
      }
      if (payload.selectedAgent) parts.push(`\n---\n${frameInstruction}`);

      await postComment(
        currentProject.id,
        taskId,
        parts.join("\n"),
        payload.selectedAgent?.id ?? null,
        payload.imageIds,
        frameId,
      );
    },
    [currentProject, frameId, frameInstruction, allTasks],
  );

  const requestAgentFollowUp = useCallback(
    async (payload: PendingSubmission) => {
      if (!currentProject || !currentSpace) {
        throw new Error(translate(hooksMessages, "openFrameProjectFirst"));
      }
      const route = await getFrameReplyRoute(
        currentProject.id,
        currentSpace.id,
        frameId,
      );
      if (
        route.conversation_id &&
        !route.native_child &&
        route.agent_key?.toLowerCase() !== "denker"
      ) {
        throw new Error(translate(hooksMessages, "frameAgentUnavailable"));
      }
      const parts = [
        `User comment on frame "${frameTitle}" (${frameType}, ${frameId}):`,
      ];
      if (payload.selectedAgent)
        parts.push(`Requested agent: ${payload.selectedAgent.name}`);
      parts.push(`Comment:\n${payload.text.trim()}`);
      if (payload.mentionedFrames.length > 0) {
        parts.push("Mentioned frames:");
        for (const frame of payload.mentionedFrames) {
          parts.push(
            `- ${frame.title} (${frame.frame_type}, ${frame.frame_id})`,
          );
        }
      }
      if (payload.imageIds?.length) {
        parts.push(`Attached file IDs: ${payload.imageIds.join(", ")}`);
      }
      parts.push(
        "Decide whether this needs a task or an update to the frame. Do not create a task solely because this is a comment.",
      );
      parts.push(frameInstruction);
      parts.push(
        `If you create a task from this comment, use create_task with frame_id="${frameId}", source="comment_mention"${payload.imageIds?.length ? `, image_ids=${JSON.stringify(payload.imageIds)}` : ""}.`,
      );
      const mentionedFrames = [
        { frame_id: frameId, title: frameTitle, frame_type: frameType },
        ...payload.mentionedFrames.filter(
          (frame) => frame.frame_id !== frameId,
        ),
      ];
      const dispatched = dispatchUserMessage({
        text: parts.join("\n\n"),
        literalText: true,
        position: currentFrameCenter(frameId),
        conversationId: route.conversation_id ?? undefined,
        startNewConversation: !route.conversation_id,
        keepConversation: true,
        nativeChildInstruction: route.native_child === true,
        imageIds: payload.imageIds,
        mentionedFrames,
        composerId: `frame-comment:${frameId}`,
        ignoreInputBarSessionState: true,
      });
      if (!dispatched.sent || !(await dispatched.accepted)) {
        throw new Error(translate(hooksMessages, "commentNotReceived"));
      }
    },
    [
      currentProject,
      currentSpace,
      frameId,
      frameTitle,
      frameType,
      frameInstruction,
    ],
  );

  /** Clear the text + mention + task-selection state after a successful
   * send. Does NOT dismiss the disambiguator — callers handle that. */
  const resetDraft = useCallback(() => {
    setText("");
    setSelectedAgent(null);
    setSelectedTaskId(null);
    setShowMentions(false);
    setMentionFilter("");
  }, []);

  const submit = useCallback(
    async (opts?: { imageIds?: string[] }) => {
      const empty = !text.trim() && !opts?.imageIds?.length;
      if (!currentProject || !currentSpace || empty || isSending) return false;

      const payload: PendingSubmission = {
        text,
        selectedAgent,
        imageIds: opts?.imageIds,
        mentionedFrames: parseFrameMentions(text, frameOptions),
      };

      // Resolve target: explicit pick → most-recently-updated linked task
      // → ask the agent. An explicit pick only falls through when the task
      // has been DELETED (not merely closed) — replying to a done thread
      // is a legitimate action (e.g. scoped-popover badge click).
      let targetTaskId: string | null = null;
      if (selectedTaskId) {
        const pickedTask = allTasks.find((t) => t.id === selectedTaskId);
        if (!pickedTask) {
          setSelectedTaskId(null);
        } else {
          targetTaskId = selectedTaskId;
        }
      }
      if (!targetTaskId && linkedTasks.length > 0) {
        targetTaskId = linkedTasks[0]!.id;
      }

      setIsSending(true);
      try {
        if (targetTaskId) {
          await postCommentOnTask(targetTaskId, payload);
          useTasksStore.getState().triggerRefetch();
        } else {
          await requestAgentFollowUp(payload);
        }
        resetDraft();
        return true;
      } catch (error) {
        useNotificationStore.getState().addSystemToast({
          id: `frame-comment-fail-${Date.now()}`,
          level: "error",
          title: translate(hooksMessages, "commentSendFailed"),
          description:
            error instanceof Error
              ? error.message
              : translate(hooksMessages, "tryAgain"),
        });
        return false;
      } finally {
        setIsSending(false);
      }
    },
    [
      currentProject,
      currentSpace,
      text,
      isSending,
      selectedAgent,
      frameOptions,
      selectedTaskId,
      linkedTasks,
      allTasks,
      postCommentOnTask,
      requestAgentFollowUp,
      resetDraft,
    ],
  );

  return {
    text,
    setText,
    selectedAgent,
    showMentions,
    mentionFilter,
    mentionIndex,
    filteredAgents,
    mentionOptions,
    handleTextChange,
    handleMentionKeyDown,
    selectAgent,
    selectMentionOption,
    clearAgent,
    linkedTasks,
    selectedTaskId,
    setSelectedTaskId,
    canSend,
    isSending,
    submit,
  };
}
